// HTTP-инфраструктура API-клиента: request/requestBlob, авторизация,
// таймауты. Вынесено из api.ts (Фаза 1, разбор недифференцированного
// клиента). Доменные модули api/ импортируют отсюда request/requestBlob.

import { devBrowserAuthUserId } from "../devBrowser";
import { waitForTelegramInitData } from "../telegram";

interface ApiError {
  code: string;
  message: string;
}

export const JSON_REQUEST_TIMEOUT_MS = 20_000;
export const LONG_ACTION_TIMEOUT_MS = 180_000;
export const MEDIA_REQUEST_TIMEOUT_MS = 45_000;

export async function request<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs = JSON_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const headers = await authorizedHeaders(options.headers);
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !headers.has("Content-Type")
  )
    headers.set("Content-Type", "application/json");
  const response = await fetchWithTimeout(
    path,
    { ...options, headers },
    timeoutMs,
  );
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as ApiError | null;
    throw new Error(error?.message ?? `Ошибка HTTP ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function requestBlob(
  path: string,
  options: RequestInit = {},
): Promise<Blob> {
  const headers = await authorizedHeaders(options.headers);
  const response = await fetchWithTimeout(
    path,
    { ...options, headers },
    MEDIA_REQUEST_TIMEOUT_MS,
  );
  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as ApiError | null;
    throw new Error(error?.message ?? `Ошибка HTTP ${response.status}`);
  }
  return response.blob();
}

async function authorizedHeaders(initial?: HeadersInit): Promise<Headers> {
  const headers = new Headers(initial);
  const initData = await waitForTelegramInitData();
  if (initData) {
    headers.set("X-Telegram-Init-Data", initData);
    return headers;
  }
  const devTelegramUserId = devBrowserAuthUserId() ?? readLegacyDevTelegramUserId();
  if (devTelegramUserId) {
    headers.set("X-Dev-Telegram-User-Id", devTelegramUserId);
    return headers;
  }
  throw new Error(
    "Telegram не передал данные авторизации. Закройте Mini App и откройте игру повторно через кнопку бота.",
  );
}

function readLegacyDevTelegramUserId(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    const value = window.localStorage.getItem("devTelegramUserId")?.trim();
    return value && /^\d{3,18}$/.test(value) ? value : null;
  } catch {
    return null;
  }
}

async function fetchWithTimeout(
  path: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const externalSignal = options.signal;
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else
      externalSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
  }
  try {
    return await fetch(path, {
      cache: "no-store",
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted)
      throw new Error(
        "Сервер не ответил вовремя. Проверьте соединение и нажмите «Повторить».",
        { cause: error },
      );
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

